/**
 * "Music We Love" page – curated concert video collection.
 * Displays a retro CRT TV component that plays YouTube embeds,
 * with a thumbnail grid below for browsing 70+ live recordings
 * from Hans-Joachim's YouTube archive and band favourites.
 */
import { useState, useMemo, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import Footer from "@/components/Footer";
import GrainOverlay from "@/components/GrainOverlay";
import PageFrame from "@/components/PageFrame";
import VariableHeadline from "@/components/VariableHeadline";
import RetroTV from "@/components/tv/RetroTV";
import VideoStack from "@/components/tv/VideoStack";
import ChatVideoSection from "@/components/tv/ChatVideoSection";
import hero08 from "@/assets/hero/hero-08.jpg?format=webp&quality=85";

// Complete video collection from Hans-Joachim's YouTube archive + band videos
const videos = [
  // User-specified videos
  { id: "OYzHgnv82RI", title: "Too Many Zooz", artist: "Berlin, YAAM (2015)", category: "Brass" },
  { id: "QkWFqcm2a78", title: "Devon Allman – No Woman No Cry", artist: "Berlin, Quasimodo (2016)", category: "Rock" },

  // === 2026 ===
  { id: "e2lZd4glftQ", title: "Samantha Fish", artist: "Berlin, Columbia Theater (2026)", category: "Blues" },
  { id: "LUDIV5xES0U", title: "Zac Schulze Gang", artist: "Berlin, Columbia Theater (2026)", category: "Rock" },

  // === 2025 ===
  { id: "ub3UTRJI-mM", title: "Christone Kingfish Ingram", artist: "Berlin, Columbia Theater (2025)", category: "Blues" },
  { id: "epiV38gs3c4", title: "V&S Blues", artist: "Frankfurt, Stormy Sunday (2025)", category: "Blues" },
  { id: "IR1HzqgLUMQ", title: "Blackhaus Bluesband", artist: "Frankfurt, Stormy Sunday (2025)", category: "Blues" },
  { id: "p6Fgry_1YQE", title: "Archy's Blues Company", artist: "Frankfurt, Stormy Sunday (2025)", category: "Blues" },
  { id: "hF8x1PhsJmI", title: "Lettuce", artist: "Berlin, Lido (2025)", category: "Funk" },

  // === 2024 ===
  { id: "NIy1tGpDQnY", title: "Dragon Smoke (Big Horse)", artist: "New Orleans, Jazz Fest (2024)", category: "Jazz" },
  { id: "y6sSiSsMIQc", title: "Ben Poole (Full Show)", artist: "Berlin, Hafenbar Tegel (2024)", category: "Blues" },
  { id: "ZfQVGDCyvIs", title: "Trombone Shorty & Orleans Avenue", artist: "New Orleans, Tipitinas (2024)", category: "Funk" },
  { id: "FHwrxWkyoAQ", title: "Gov't Mule", artist: "New Orleans, Daze Between (2024)", category: "Rock" },
  { id: "NPfIabEBL5E", title: "Henrik Freischlader", artist: "Berlin, Kesselhaus (2024)", category: "Blues" },
  { id: "jLNalXCZQAA", title: "Kofi Baker's Cream Faith", artist: "Berlin, Hafenbar Tegel (2024)", category: "Rock" },

  // === 2023 ===
  { id: "2SnDsCXe7-I", title: "A·B·C – The Bluest Blues", artist: "Frankfurt, Stormy Monday (2023)", category: "Blues" },
  { id: "r4Pi5uQcBLQ", title: "Paul Hilger & The Kingpins", artist: "Frankfurt, Stormy Monday (2023)", category: "Blues" },
  { id: "0vRbGtrqZKc", title: "Unchained", artist: "Frankfurt, Stormy Monday (2023)", category: "Rock" },
  { id: "QbKM_tL6bk8", title: "Stormy Monday Summer Special", artist: "Frankfurt (2023)", category: "Blues" },
  { id: "aqfPSXGOalY", title: "A·B·C", artist: "Frankfurt, Stormy Monday (2023)", category: "Blues" },
  { id: "0Midn0tqK_I", title: "Matchbox Bluesband", artist: "Frankfurt, Stormy Monday (2023)", category: "Blues" },
  { id: "t7fSKPfb4Jk", title: "Dusk Plays Nirvana Unplugged", artist: "Potsdam, Lindenpark (2023)", category: "Rock" },
  { id: "BmYKPSKnMyU", title: "Dusk Plays Nirvana Plugged", artist: "Potsdam, Lindenpark (2023)", category: "Rock" },
  { id: "S-LMBAIAqvo", title: "Kenny Wayne Shepherd", artist: "Berlin, Verti Music Hall (2023)", category: "Blues" },
  { id: "KJ26xUn83e4", title: "Samantha Fish & Jesse Dayton", artist: "Berlin, Hole44 (2023)", category: "Blues" },

  // === 2022 ===
  { id: "Hfo7mhTq6Ig", title: "Snarky Puppy", artist: "Berlin (2022)", category: "Jazz" },
  { id: "oVuCoWXeNrA", title: "Trombone Shorty & Orleans Avenue", artist: "Berlin (2022)", category: "Funk" },

  // === 2021 (Drum Covers by Hajobu) ===
  { id: "sFxdKomPBWk", title: "Hajobu – ZZ Top 'Sharp Dressed Man'", artist: "Drum Cover (2021)", category: "Covers" },
  { id: "2baPe-xcl6U", title: "Hajobu – Chuck Berry 'Johnny B Goode'", artist: "Drum Cover (2021)", category: "Covers" },
  { id: "9fgDzvZVT-4", title: "Hajobu – P!NK 'Try'", artist: "Drum Cover (2021)", category: "Covers" },
  { id: "Idxx4Hlninc", title: "Hajobu – Eagles 'Hotel California'", artist: "Drum Cover (2021)", category: "Covers" },

  // === 2019 ===
  { id: "VQFrE7MXOIQ", title: "Franky Dickens Band", artist: "Berlin, Die Kiste (2019)", category: "Blues" },
  { id: "x0ATpKC9_qo", title: "East Blues Experience", artist: "Berlin, Parkbühne Biesdorf (2019)", category: "Blues" },
  { id: "bsk8yc8Nvps", title: "Henrik Freischlader Band", artist: "Berlin, Quasimodo (2019)", category: "Blues" },
  { id: "tTj5ixD0jEo", title: "Pillbugz Blues", artist: "Berlin, Kultur Keller (2019)", category: "Blues" },
  { id: "ImjiYstx_W8", title: "Chris Thompson & Mads Eriksen Band", artist: "Berlin, Biesdorfer Parkbühne (2019)", category: "Rock" },
  { id: "gfVqOrjp9is", title: "Trombone Shorty & Aaron Neville", artist: "New Orleans, Jazz Fest (2019)", category: "Funk" },
  { id: "creVA1tjnUY", title: "Midnite Disturbers", artist: "New Orleans, Jazz Fest (2019)", category: "Jazz" },
  { id: "Yf_dwq6Vr7o", title: "Meute", artist: "Berlin, Huxleys Neue Welt (2019)", category: "Live" },
  { id: "hDC46N1BB9k", title: "Tal Wilkenfeld", artist: "Berlin, Privatclub (2019)", category: "Rock" },
  { id: "IzocpUwGSL8", title: "Raul Midón", artist: "Berlin, Quasimodo (2019)", category: "Jazz" },

  // === 2018 ===
  { id: "b-vX9lyVlSw", title: "Die Zöllner", artist: "Berlin, Kesselhaus (2018)", category: "Rock" },
  { id: "B9DI1HVWtDw", title: "Rob Tognoni", artist: "Berlin, Die Kiste (2018)", category: "Blues" },
  { id: "meM0pcZG_oM", title: "Broken Brass Ensemble", artist: "Berlin, Quasimodo (2018)", category: "Brass" },
  { id: "-GhSaGem-K0", title: "Leif de Leeuw Band", artist: "Berlin, Hellersdorf (2018)", category: "Rock" },
  { id: "tw1orAG4X44", title: "Eric Steckel", artist: "Berlin, Hellersdorf (2018)", category: "Rock" },
  { id: "e3aAwQC18CI", title: "Julian Sas Band", artist: "Berlin, Biesdorfer Parkbühne (2018)", category: "Rock" },
  { id: "S_l0a2YE9e0", title: "Quinn Sullivan / Eric Gales / Gary Hoey", artist: "Berlin, Rockin' the Blues (2018)", category: "Rock" },
  { id: "x8XreXCg7fY", title: "Manu Delago", artist: "Berlin, Quasimodo (2018)", category: "Live" },
  { id: "vfGjvuguzJU", title: "Blues Caravan", artist: "Berlin, Quasimodo (2018)", category: "Blues" },

  // === 2017 ===
  { id: "Rx6e2jk46hE", title: "Samantha Fish", artist: "Berlin, Quasimodo (2017)", category: "Blues" },
  { id: "KrXZhAB71PM", title: "Trombone Shorty & Orleans Avenue", artist: "Berlin, Columbia Theater (2017)", category: "Funk" },
  { id: "DSfGvTT7Sgk", title: "Marcus Miller", artist: "Berlin, Huxleys Neue Welt (2017)", category: "Jazz" },
  { id: "gXsjF17gsDs", title: "Jonny Lang", artist: "Berlin, Columbia Theater (2017)", category: "Blues" },
  { id: "otaqqNVNqPI", title: "Trombone Shorty & Orleans Avenue ⭐", artist: "Erlangen, E-Werk (2017)", category: "Funk" },
  { id: "aYPJmuq7LSA", title: "Jimmy Reiter Band", artist: "Frankfurt, Höchst Bluesig (2017)", category: "Blues" },
  { id: "Q_hr6jUi2rE", title: "Samantha Fish", artist: "New Orleans, Republik (2017)", category: "Blues" },
  { id: "rkv-IqRFM6s", title: "Samantha Fish & Tab Benoit", artist: "New Orleans, Republik (2017)", category: "Blues" },
  { id: "W5o0SkSiNik", title: "Tab Benoit", artist: "New Orleans, Jazz Fest (2017)", category: "Blues" },
  { id: "Ez6dkeqJ21Y", title: "Dukes of Dixieland", artist: "New Orleans, Jazz Fest (2017)", category: "Jazz" },
  { id: "xiL0rO6uzwk", title: "Kris Pohlmann – One for Sorrow", artist: "Berlin, Kiste (2017)", category: "Blues" },
  { id: "AiwspDpGKJA", title: "Kris Pohlmann – Volatile Skies", artist: "Berlin, Kiste (2017)", category: "Blues" },
  { id: "VBILX1Idb0c", title: "Kris Pohlmann – Heavy on My Soul", artist: "Berlin, Kiste (2017)", category: "Blues" },
  { id: "rx9YjLny8YY", title: "Tommy Schneller Band – Duo", artist: "Berlin, Quasimodo (2017)", category: "Blues" },
  { id: "tIOnYZwIRWM", title: "Tommy Schneller Band – Smiling for a Reason", artist: "Berlin, Quasimodo (2017)", category: "Blues" },
  { id: "T8g7LGQhtmo", title: "Tommy Schneller Band – Cleaning Lady Blues", artist: "Berlin, Quasimodo (2017)", category: "Blues" },
  { id: "AEOW-LSU7QA", title: "Kofi Baker's Cream Experience – Outside Woman Blues", artist: "Berlin, Kiste (2017)", category: "Rock" },
  { id: "yxSabWGty8M", title: "Kofi Baker's Cream Experience – Born Under a Bad Sign", artist: "Berlin, Kiste (2017)", category: "Rock" },
  { id: "BOJDFE3h8DU", title: "Kofi Baker's Cream Experience – Little Wing", artist: "Berlin, Kiste (2017)", category: "Rock" },
  { id: "v6IgbJao0x0", title: "Kofi Baker", artist: "Berlin, Die Kiste (2017)", category: "Rock" },

  // === 2016 ===
  { id: "EuRbY_FIhFA", title: "Henrik Freischlader Trio – Bad Dreams/Wolkenwinde", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "lvER3k5uACk", title: "Henrik Freischlader Trio – Never Really Left You", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "IWY1tX0w8tk", title: "Henrik Freischlader Trio – Longer Days", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "hSfZ6PCNuV8", title: "Henrik Freischlader Trio – Masterplan", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "A5fUNB8S3MM", title: "Mike Zito – Dirty Blonde Hair", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "nRIMpy52ds0", title: "Mike Zito – Bad News Is Coming", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "HFNhIg6TaVk", title: "Mike Zito – Make Blues Not War", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "fYpFvfRUQ2w", title: "Devon Allman Band – Checking on My Baby", artist: "Berlin, Quasimodo (2016)", category: "Rock" },
  { id: "XdCk0wLJxv4", title: "Devon Allman Band – Midnight Lake Michigan", artist: "Berlin, Quasimodo (2016)", category: "Rock" },
  { id: "FFH5Zsw8pNw", title: "Devon Allman Band – Maholo", artist: "Berlin, Quasimodo (2016)", category: "Rock" },
  { id: "Ey5JAiNvwNQ", title: "Kamchatka", artist: "Berlin, Biesdorfer Parkbühne (2016)", category: "Rock" },
  { id: "mwP9w95Icx4", title: "Ben Granfelt – Hush", artist: "Berlin, Biesdorfer Parkbühne (2016)", category: "Rock" },
  { id: "q-TBL7akbdo", title: "Miss Zippi & The Blues Wail", artist: "Frankfurt, Höchst Bluesig (2016)", category: "Blues" },
  { id: "ZB7-cD13xOg", title: "3220 Bluesband", artist: "Frankfurt, Höchst Bluesig (2016)", category: "Blues" },
  { id: "sGC98Fwn4yU", title: "Höchst Allstars", artist: "Frankfurt, Höchst Bluesig (2016)", category: "Blues" },
  { id: "IRWFKZKz4nw", title: "Down Home Percolators feat. Holger Schultze", artist: "Frankfurt, Höchst Bluesig (2016)", category: "Blues" },
  { id: "YZT4Qa2Wgvo", title: "Kris Pohlmann feat. Fred Hauschild", artist: "Frankfurt, Höchst Bluesig (2016)", category: "Blues" },
  { id: "tbXsFb0Bwds", title: "Kris Pohlmann feat. Fred Hauschild – Too Tired", artist: "Frankfurt, Höchst Bluesig (2016)", category: "Blues" },
  { id: "vIrhjp1JBiU", title: "Henrik Freischlader Trio – High Expectations", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "9hwMnbsqY1E", title: "Henrik Freischlader Trio – Lord Have Mercy", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "X62-OXNchSk", title: "Henrik Freischlader Trio – Techno", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "u9Olh2pMpOQ", title: "Henrik Freischlader Trio – Bad Dreams/Wolkenwinde", artist: "Berlin, Quasimodo (2016)", category: "Blues" },
  { id: "_Em10_iblBU", title: "Royal Southern Brotherhood – Slow Blues", artist: "Berlin, Quasimodo (2016)", category: "Rock" },
  { id: "shrGzyrCS5o", title: "Royal Southern Brotherhood – Blood Is Thicker Than Water", artist: "Berlin, Quasimodo (2016)", category: "Rock" },

  // === 2015 ===
  { id: "R3torKIoZx4", title: "Henrik Freischlader Trio – Full Concert", artist: "Berlin, Tempodrom (2015)", category: "Blues" },
  { id: "BzeWy4Pjn5w", title: "Henrik Freischlader Trio – Early Morning Blues", artist: "Berlin, Tempodrom (2015)", category: "Blues" },
  { id: "fQrlcf_vPYE", title: "Henrik Freischlader Trio – Masterplan", artist: "Berlin, Tempodrom (2015)", category: "Blues" },
  { id: "8xsktRtQdt4", title: "Andy Fairweather Low & The Low Riders", artist: "Berlin, Frannz Club (2015)", category: "Live" },
  { id: "xMxb75Zamb0", title: "Warren Haynes & Ashes & Dust Band – Jessica", artist: "Berlin, Kesselhaus (2015)", category: "Rock" },
  { id: "w_5DUBIIdvA", title: "Tedeschi Trucks Band – The Storm", artist: "Berlin, Tempodrom (2015)", category: "Rock" },

  // === 2014 ===
  { id: "dGxLaxLTotU", title: "Mike Zito", artist: "Berlin, Quasimodo (2014)", category: "Blues" },

  // === 2013 ===
  { id: "EHRBWJ83mHQ", title: "Popa Chubby", artist: "Berlin, Postbahnhof (2013)", category: "Blues" },
];

const MusicWeLove = () => {
  useDocumentMeta({
    title: "Music We Love – Thursday Jam",
    description: "A curated collection of live concert recordings that inspire Thursday Jam's sound. Blues, rock, funk, and jazz from around the world.",
    canonical: "https://thursdayjam.rocks/music-we-love",
    ogImage: hero08,
  });
  const tvRef = useRef<HTMLDivElement>(null);
  // Pick a random video on initial load
  const initialVideoId = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * videos.length);
    return videos[randomIndex].id;
  }, []);
  
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(initialVideoId);
  const [autoplay, setAutoplay] = useState(false);

  const handleChannelChange = useCallback((direction: "up" | "down") => {
    const currentIndex = videos.findIndex(v => v.id === selectedVideoId);
    let newIndex: number;
    
    if (direction === "up") {
      newIndex = currentIndex < videos.length - 1 ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : videos.length - 1;
    }
    
    setAutoplay(true);
    setSelectedVideoId(videos[newIndex].id);
  }, [selectedVideoId]);

  const handleThumbnailSelect = useCallback((id: string) => {
    setAutoplay(true);
    setSelectedVideoId(id);
    tvRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  return (
    <main id="main-content" className="relative min-h-screen bg-background">
      <PageFrame />
      <GrainOverlay />
      <Navigation variant="about" />

      {/* Hero section - matching Videos/Gallery style */}
      <section className="relative pt-32 pb-16 px-6 md:px-24 lg:px-32">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mb-6 h-px w-24 origin-left bg-foreground/30"
          />
          <VariableHeadline 
            as="h1" 
            pattern="crescendo"
            className="text-5xl md:text-7xl lg:text-8xl mb-4"
          >
            Music We Love
          </VariableHeadline>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="text-lg text-muted-foreground"
          >
            A curated collection of the music that inspires our sound
          </motion.p>
        </div>
      </section>

      {/* Main content - TV on top, Video Stack below */}
      <section className="relative px-6 md:px-24 lg:px-32 pb-24">
        <div className="max-w-4xl mx-auto space-y-16">
          {/* Retro TV Player - Larger and centered */}
          <motion.div
            ref={tvRef}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <RetroTV 
              videoId={selectedVideoId} 
              isPlaying={autoplay} 
              onChannelChange={handleChannelChange}
            />
            
            {/* Now Playing info */}
            {selectedVideoId && (
              <motion.div
                key={selectedVideoId}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-6 text-center"
              >
                <p className="font-display text-lg md:text-xl uppercase tracking-wide">
                  {videos.find(v => v.id === selectedVideoId)?.title}
                </p>
                <p className="text-sm text-muted-foreground">
                  {videos.find(v => v.id === selectedVideoId)?.artist}
                </p>
              </motion.div>
            )}
          </motion.div>

          {/* Video thumbnail stack - Below TV */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <VideoStack
              videos={videos}
              selectedId={selectedVideoId}
              onSelect={handleThumbnailSelect}
            />
          </motion.div>

          {/* Group Chat Picks - Below main archive */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <ChatVideoSection
              selectedId={selectedVideoId}
              onSelect={handleThumbnailSelect}
            />
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default MusicWeLove;